// client/src/components/hub/HubSettingsPage.jsx
//
// /:tenant/public-hub — staff settings for the tenant's public hub page
// (/hub/:tenantSlug): on/off, title and description for search and link
// previews, colours, which sections show, custom links, socials. Also the
// share box: hub link, events embed snippet (list / calendar) with a live
// preview, sitemap and calendar-feed URLs. Growth Phase 8, base package.
//
// API: GET/PUT /api/tenants/:tenant/hub-settings (routes/hubSettingsRoutes.js)

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaHome } from 'react-icons/fa';
import AppFooter from '../AppFooter';
import LoadingState from '../common/LoadingState';
import ErrorState from '../common/ErrorState';
import { CopyRow } from '../forms/FormEditor';
import {
  embedEventsUrl, embedSnippet, getHubSettings, saveHubSettings, SECTION_KEYS, SOCIAL_PLATFORMS,
} from '../../services/hubApi';
import '../analytics/PerformancePanel.css';
import '../forms/Forms.css';

const MAX_LINKS = 20;

function toDraft(s) {
  return {
    enabled: !!s.enabled,
    seoTitle: s.seoTitle || '',
    seoDescription: s.seoDescription || '',
    accent: s.theme?.accent || '#253866',
    mode: s.theme?.mode === 'dark' ? 'dark' : 'light',
    sections: { ...s.sections },
    customLinks: (s.customLinks || []).map((l) => ({ ...l })),
    socials: { ...(s.socials || {}) },
  };
}

function toBody(d) {
  return {
    enabled: d.enabled,
    seoTitle: d.seoTitle,
    seoDescription: d.seoDescription,
    theme: { accent: d.accent, mode: d.mode },
    sections: d.sections,
    customLinks: d.customLinks.filter((l) => l.label.trim() || l.url.trim()),
    socials: d.socials,
  };
}

export default function HubSettingsPage({ tenantId: tenantProp }) {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const tenant = Number(tenantProp || params.tenant);

  const [draft, setDraft] = useState(null);
  const [slug, setSlug] = useState(null);
  const [urls, setUrls] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null); // { kind: 'ok' | 'error', text, errors? }
  const [embed, setEmbed] = useState({ view: 'list', limit: 5, theme: '' });

  const sectionLabel = (k) => ({
    live: t('hub.section.live', 'Happening now'),
    upcoming: t('hub.section.upcoming', 'Upcoming events'),
    past: t('hub.section.past', 'Past events'),
    links: t('hub.section.links', 'Custom links'),
    forms: t('hub.section.forms', 'Forms (attached to "Public page")'),
    socials: t('hub.section.socials', 'Social profiles'),
  }[k] || k);

  const load = useCallback(async () => {
    setLoadError('');
    try {
      const res = await getHubSettings(tenant);
      setDraft(toDraft(res.settings));
      setSlug(res.tenantSlug);
      setUrls(res.urls);
    } catch (err) {
      setLoadError(/^409/.test(err?.message || '')
        ? t('hub.notReady', 'The public page needs a database update before it can be used.')
        : t('hub.loadError', 'Could not load the public page settings.'));
    }
  }, [tenant, t]);
  useEffect(() => { load(); }, [load]);

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const setLink = (i, patch) => set({ customLinks: draft.customLinks.map((l, j) => (j === i ? { ...l, ...patch } : l)) });
  const moveLink = (i, dir) => {
    const next = [...draft.customLinks];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    set({ customLinks: next });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setNotice(null);
    try {
      const res = await saveHubSettings(tenant, toBody(draft));
      setDraft(toDraft(res.settings));
      setUrls(res.urls);
      setSlug(res.tenantSlug);
      setNotice({ kind: 'ok', text: res.settings.enabled ? t('hub.savedLive', 'Saved. Your public page is live.') : t('hub.savedOff', 'Saved. Your public page is off.') });
    } catch (err) {
      setNotice({
        kind: 'error',
        text: err?.response?.data?.message || t('hub.saveError', 'Could not save.'),
        errors: err?.response?.data?.errors || [],
      });
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <AppFooter
      buttons={[
        { icon: <FaArrowLeft />, label: t('common.back', 'Back'), onClick: () => navigate(-1) },
        { icon: <FaHome />, label: t('common.home', 'Home'), onClick: () => navigate(`/${tenant}/dashboard`) },
      ]}
    />
  );

  if (loadError) return <div className="fo-page"><ErrorState message={loadError} onRetry={load} />{footer}</div>;
  if (!draft) return <div className="fo-page"><LoadingState />{footer}</div>;

  const embedSrc = slug ? embedEventsUrl(slug, embed) : null;

  return (
    <div className="fo-page">
      <section className="surface">
        <h2>{t('hub.title', 'Public page')}</h2>
        <p className="surface-muted perf-small">
          {t('hub.intro', 'One link for your bio or email signature: what\'s on now, upcoming public events, your links, forms and social profiles. Only events set to "Public" on their public page settings appear here.')}
        </p>
        {urls?.hub && (
          <div className={`note ${draft.enabled ? 'ok' : 'info'}`}>
            {draft.enabled
              ? <>{t('hub.statusLive', 'Live at')} <a href={urls.hub} target="_blank" rel="noreferrer">{urls.hub}</a></>
              : t('hub.statusOff', 'Off. Turn it on below to publish {{url}}.', { url: urls.hub })}
          </div>
        )}
        {!slug && <div className="note warn">{t('hub.noSlug', 'Your organization needs a URL slug before this page can go live.')}</div>}
      </section>

      <form onSubmit={save} className="fo-grid">
        <section className="surface">
          <h4>{t('hub.basics', 'Page')}</h4>
          <label className="fo-check">
            <input type="checkbox" checked={draft.enabled} onChange={(e) => set({ enabled: e.target.checked })} />
            {t('hub.enabled', 'Publish my public page')}
          </label>
          <div className="fo-form-grid">
            <label className="fo-field">
              {t('hub.seoTitle', 'Title (search results and link previews)')}
              <input value={draft.seoTitle} maxLength={70} onChange={(e) => set({ seoTitle: e.target.value })}
                placeholder={t('hub.seoTitlePlaceholder', 'Defaults to your organization name')} />
            </label>
            <label className="fo-field">
              {t('hub.seoDescription', 'Description (search results and link previews)')}
              <textarea rows={2} value={draft.seoDescription} maxLength={200} onChange={(e) => set({ seoDescription: e.target.value })}
                placeholder={t('hub.seoDescriptionPlaceholder', 'Defaults to your organization description')} />
            </label>
            <label className="fo-field">
              {t('hub.accent', 'Accent colour')}
              <input type="color" value={draft.accent} onChange={(e) => set({ accent: e.target.value })} />
            </label>
            <label className="fo-field">
              {t('hub.mode', 'Background')}
              <select value={draft.mode} onChange={(e) => set({ mode: e.target.value })}>
                <option value="light">{t('hub.modeLight', 'Light')}</option>
                <option value="dark">{t('hub.modeDark', 'Dark')}</option>
              </select>
            </label>
          </div>
        </section>

        <section className="surface">
          <h4>{t('hub.sections', 'Sections')}</h4>
          <div className="fo-form-grid">
            {SECTION_KEYS.map((k) => (
              <label key={k} className="fo-check">
                <input type="checkbox" checked={!!draft.sections[k]}
                  onChange={(e) => set({ sections: { ...draft.sections, [k]: e.target.checked } })} />
                {sectionLabel(k)}
              </label>
            ))}
          </div>
        </section>

        <section className="surface">
          <h4>{t('hub.links', 'Custom links')}</h4>
          <p className="surface-muted perf-small">{t('hub.linksHelp', 'Shown as buttons, in this order. Up to 20.')}</p>
          <ul className="fo-fields">
            {draft.customLinks.map((l, i) => (
              <li key={i} className="fo-row">
                <label className="fo-field">
                  {t('hub.linkLabel', 'Label')}
                  <input value={l.label} maxLength={60} onChange={(e) => setLink(i, { label: e.target.value })} />
                </label>
                <label className="fo-field">
                  {t('hub.linkUrl', 'URL')}
                  <input type="url" value={l.url} placeholder="https://" onChange={(e) => setLink(i, { url: e.target.value })} />
                </label>
                <span />
                <div className="fo-row-actions">
                  <button type="button" className="lm-btn secondary" onClick={() => moveLink(i, -1)} disabled={i === 0}
                    aria-label={t('hub.moveUp', 'Move up')}>↑</button>
                  <button type="button" className="lm-btn secondary" onClick={() => moveLink(i, 1)} disabled={i === draft.customLinks.length - 1}
                    aria-label={t('hub.moveDown', 'Move down')}>↓</button>
                  <button type="button" className="lm-btn secondary" onClick={() => set({ customLinks: draft.customLinks.filter((_, j) => j !== i) })}>
                    {t('hub.remove', 'Remove')}
                  </button>
                </div>
              </li>
            ))}
          </ul>
          <button type="button" className="lm-btn secondary" disabled={draft.customLinks.length >= MAX_LINKS}
            onClick={() => set({ customLinks: [...draft.customLinks, { label: '', url: '' }] })}>
            {t('hub.addLink', 'Add link')}
          </button>
        </section>

        <section className="surface">
          <h4>{t('hub.socials', 'Social profiles')}</h4>
          <div className="fo-form-grid">
            {SOCIAL_PLATFORMS.map((k) => (
              <label key={k} className="fo-field">
                {t(`hub.social.${k}`, k)}
                <input type="url" value={draft.socials[k] || ''} placeholder="https://"
                  onChange={(e) => set({ socials: { ...draft.socials, [k]: e.target.value } })} />
              </label>
            ))}
          </div>
        </section>

        <section className="surface">
          {notice && (
            <div className={`note ${notice.kind === 'ok' ? 'ok' : 'error'}`} role={notice.kind === 'error' ? 'alert' : 'status'}>
              {notice.text}
              {notice.errors?.length > 0 && (
                <ul>{notice.errors.map((x) => <li key={x.field}>{x.field}: {x.message}</li>)}</ul>
              )}
            </div>
          )}
          <button type="submit" className="lm-btn" disabled={saving}>
            {saving ? t('hub.saving', 'Saving…') : t('hub.save', 'Save')}
          </button>
        </section>
      </form>

      {slug && urls && (
        <section className="surface">
          <h4>{t('hub.share', 'Share and embed')}</h4>
          <CopyRow label={t('hub.shareLink', 'Public page link')} value={urls.hub} />
          <div className="fo-form-grid">
            <label className="fo-field">
              {t('hub.embedView', 'Embed layout')}
              <select value={embed.view} onChange={(e) => setEmbed({ ...embed, view: e.target.value })}>
                <option value="list">{t('hub.embedList', 'List')}</option>
                <option value="calendar">{t('hub.embedCalendar', 'Calendar')}</option>
              </select>
            </label>
            {embed.view === 'list' && (
              <label className="fo-field">
                {t('hub.embedLimit', 'Number of events')}
                <input type="number" min={1} max={50} value={embed.limit}
                  onChange={(e) => setEmbed({ ...embed, limit: Math.min(50, Math.max(1, Number(e.target.value) || 1)) })} />
              </label>
            )}
            <label className="fo-field">
              {t('hub.embedTheme', 'Embed background')}
              <select value={embed.theme} onChange={(e) => setEmbed({ ...embed, theme: e.target.value })}>
                <option value="">{t('hub.embedThemeHub', 'Same as public page')}</option>
                <option value="light">{t('hub.modeLight', 'Light')}</option>
                <option value="dark">{t('hub.modeDark', 'Dark')}</option>
              </select>
            </label>
          </div>
          <CopyRow label={t('hub.embedSnippet', 'Paste this into your website')} value={embedSnippet(embedSrc, t('hub.embedTitle', 'Upcoming events'))} />
          <p className="surface-muted perf-small">
            {t('hub.embedHelp', 'The iframe resizes itself to fit. Clicks on events are counted under Links as "embed".')}
          </p>
          <div className="fo-preview">
            <iframe title={t('hub.embedPreview', 'Embed preview')} src={embedSrc} style={{ border: 0, width: '100%', height: 420, background: '#ffffff' }} loading="lazy" />
          </div>
          <CopyRow label={t('hub.sitemap', 'Sitemap (for search engines)')} value={urls.sitemap} />
          <CopyRow label={t('hub.feed', 'Calendar feed (subscribe)')} value={urls.feed} />
        </section>
      )}

      {footer}
    </div>
  );
}
